import React, { useState, useEffect } from "react";
import Footer from "./Footer";
import { CircleX } from "lucide-react";
import * as LucideIcons from "lucide-react";
import useStickyButton from "../hooks/useStickyButton";

const TakePhotoError = ({ errorMessage, imageBuffer, onRetry, uiConfig, showImage = true }) => {
  const [imageBlob, setImageBlob] = useState(null);
  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();

  const buildClassString = (classList) => classList.filter(Boolean).join(" ");

  // Safe access
  const global = uiConfig?.Global || {};
  const takePhotoError = uiConfig?.TakePhotoError || {};
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const cardBgColor = global?.card?.backgroundColor || "#f3f4f6"; // gray-50
  const cardOpacity = global?.card?.opacity || "1"; // default opacity
  const buttonBgColor = global?.button?.backgroundColor || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white
  const showFooter = global?.Footer?.Show !== undefined ? global.Footer.Show : true;

  const getButtonConfig = () => {
    const globalButton = global?.button || {};
    const buttonConfig = takePhotoError?.TryAgainButton || {};
    
    // Build base style from global button config
    const globalStyle = globalButton?.Style || `padding: 0.75rem 1rem; border-radius: 9999px; display: flex; align-items: center; justify-content: center; width: 66.67%; margin: 0 auto; background: ${buttonBgColor}; color: ${buttonTextColor}; transition: transform 0.15s ease-in-out; border: none; cursor: pointer;`;
    
    // Parse global style
    const parsedGlobalStyle = globalStyle.split(';').reduce((acc, style) => {
      const [key, value] = style.split(':').map(s => s.trim());
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    // Parse and merge specific button style if it exists
    let finalStyle = { ...parsedGlobalStyle };
    if (buttonConfig?.Style) {
      const parsedButtonStyle = buttonConfig.Style.split(';').reduce((acc, style) => {
        const [key, value] = style.split(':').map(s => s.trim());
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      }, {});
      finalStyle = { ...finalStyle, ...parsedButtonStyle };
    }
    
    // Convert back to CSS string
    const mergedStyleString = Object.entries(finalStyle)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ') + ';';
    
    return {
      text: buttonConfig?.Text || globalButton?.Text || "Try Again",
      style: mergedStyleString,
      icon: {
        show: buttonConfig?.Icon?.Show !== undefined ? buttonConfig.Icon.Show : (globalButton?.Icon?.Show !== undefined ? globalButton.Icon.Show : false),
        position: buttonConfig?.Icon?.Position || globalButton?.Icon?.Position || "end",
        name: buttonConfig?.Icon?.Name || globalButton?.Icon?.Name || "RotateCcw",
        className: buttonConfig?.Icon?.ClassName || globalButton?.Icon?.ClassName || "ml-2"
      }
    };
  };

  useEffect(() => {
    if (imageBuffer) {
      setImageBlob(new Blob([imageBuffer], { type: "image/jpeg" }));
    }
  }, [imageBuffer]);

  return (
    <div
      style={{
        background: pageBgColor,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "1rem",
        height: "100%",
      }}
    >
      <div
        style={{
          background: cardBgColor,
          opacity: cardOpacity,
          padding: "2rem",
          borderRadius: "1rem",
          marginTop: "calc(100vh / 16)",
          marginBottom: "2rem",
        }}
      >
        <div className="flex flex-col items-center mb-8">
          <CircleX height="72" width="72" color="red" />
        </div>
        <div className="flex flex-col">
          <p className={"text-2xl font-medium text-gray-800"}>Almost there! Please try again.</p>

          <p className={"mt-4 text-lg text-gray-600"}>{errorMessage}</p>
        </div>

        <div className="flex flex-col items-center mt-8">
          {showImage && imageBlob && <img src={URL.createObjectURL(imageBlob)} alt="MyKad" className="w-60" />}
        </div>
      </div>

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      <div 
        ref={buttonContainerRef}
        style={{
          position: isSticky ? "fixed" : "relative",
          bottom: isSticky ? "0" : "auto",
          left: isSticky ? "0" : "auto",
          right: isSticky ? "0" : "auto",
          padding: isSticky ? "1rem" : "0",
          background: "transparent",
          zIndex: isSticky ? "50" : "auto",
          transition: "all 0.3s ease-in-out",
          pointerEvents: isSticky ? "none" : "auto",
        }}
      >
        {(() => {
          const btnConfig = getButtonConfig();
          const IconComponent = btnConfig.icon.show && LucideIcons[btnConfig.icon.name] ? LucideIcons[btnConfig.icon.name] : null;
          
          const buttonStyle = btnConfig.style.split(';').reduce((acc, style) => {
            const [key, value] = style.split(':').map(s => s.trim());
            if (key && value) {
              const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
              acc[camelKey] = value;
            }
            return acc;
          }, {});

          return (
            <button
              style={{ ...buttonStyle, pointerEvents: 'auto' }}
              onClick={onRetry}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {btnConfig.icon.position === "start" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
              <span>{btnConfig.text}</span>
              {btnConfig.icon.position === "end" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
            </button>
          );
        })()}
      </div>

      {showFooter ? (
        <Footer
          style={{
            fontSize: "0.75rem",
            color: textColor,
            marginTop: "1rem",
          }}
        />
      ) : (
        /* Minimal spacer to prevent sticky flickering when footer is hidden */
        <div style={{ height: "5px", flexShrink: 0 }} />
      )}
    </div>
  );
};

export default TakePhotoError;
